import React, { useState } from 'react';
import { Box, Typography, Popover, Tooltip, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import moment from 'moment';
import { THEME_COLOR } from '../../theme/formStyles';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// One fixed color per schedule type
const TYPE_COLORS = {
    calibration:           { bg: '#fff3e0', color: '#c75000', border: '#ffcc80', label: 'Calibration' },
    calibration_completed: { bg: '#e8f5e9', color: '#2e7d32', border: '#81c784', label: 'Calibration' },
    opportunity:           { bg: '#f0ebff', color: '#6a3db5', border: '#c9b8f0', label: 'BD Client Meet' },
    lead:                  { bg: '#e0f7fa', color: '#00695c', border: '#80cbc4', label: 'Lead Meet' },
    project:               { bg: '#e3f2fd', color: '#1565c0', border: '#90caf9', label: 'Project Task' },
    ticket:                { bg: '#fce4ec', color: '#b71c1c', border: '#ef9a9a', label: 'Ticket' },
};

function getColors(schedule) {
    if (schedule._type === 'calibration' && schedule.status === 'Completed') {
        return TYPE_COLORS.calibration_completed;
    }
    return TYPE_COLORS[schedule._type];
}

function getSpocName(spoc) {
    if (!spoc) return '';
    if (typeof spoc === 'object') {
        // Stored/returned as a parsed object — pick the best readable field
        return String(spoc.name || spoc.user_id || spoc.id || '');
    }
    if (typeof spoc === 'string') {
        if (spoc === '[object Object]') return '';
        try {
            const parsed = JSON.parse(spoc);
            return String(parsed?.name || parsed?.user_id || parsed?.id || spoc);
        } catch {
            return spoc;
        }
    }
    return String(spoc);
}

function getAssignee(schedule) {
    if (schedule._type === 'opportunity' || schedule._type === 'lead') return schedule.bd_owner?.name || '';
    if (schedule._type === 'project') return getSpocName(schedule.spoc);
    if (schedule._type === 'ticket') return schedule.assignee || '';
    const a = schedule.assignee_name;
    return typeof a === 'object' ? (a?.name || '') : (a || '');
}

function getEventLabel(schedule) {
    const assignee = getAssignee(schedule);
    const prefix = schedule._type === 'opportunity' ? 'BD Meet'
        : schedule._type === 'lead' ? 'Lead Meet'
        : schedule._type === 'project' ? 'Project Task'
        : schedule._type === 'ticket' ? 'Ticket'
        : 'Calibration';
    return assignee ? `${prefix} – ${assignee}` : prefix;
}

export default function MasterCalendar({ month, year, schedules = [], opportunitySchedules = [], leadSchedules = [], projectSubTasks = [], ticketSchedules = [] }) {
    const [detailAnchor, setDetailAnchor] = useState(null);
    const [detailData, setDetailData] = useState(null);

    // Build calendar grid
    const firstDay = dayjs().year(year).month(month).date(1);
    const daysInMonth = firstDay.daysInMonth();
    const startDayOfWeek = firstDay.day();
    const prevMonth = firstDay.subtract(1, 'month');
    const daysInPrevMonth = prevMonth.daysInMonth();

    // Merge all schedule types into a date-keyed lookup
    const monthStart = firstDay.format('YYYY-MM-DD');
    const monthEnd = firstDay.date(daysInMonth).format('YYYY-MM-DD');

    const schedulesByDate = {};
    schedules.forEach(s => {
        const key = moment(s.schedule_date).format('YYYY-MM-DD');
        if (!schedulesByDate[key]) schedulesByDate[key] = [];
        schedulesByDate[key].push({ ...s, _type: 'calibration' });
    });
    opportunitySchedules.forEach(s => {
        const key = moment(s.schedule_date).format('YYYY-MM-DD');
        if (!schedulesByDate[key]) schedulesByDate[key] = [];
        schedulesByDate[key].push({ ...s, _type: 'opportunity' });
    });
    leadSchedules.forEach(s => {
        const key = moment(s.schedule_date).format('YYYY-MM-DD');
        if (!schedulesByDate[key]) schedulesByDate[key] = [];
        schedulesByDate[key].push({ ...s, _type: 'lead' });
    });
    ticketSchedules.forEach(s => {
        const key = moment(s.scheduled_date).format('YYYY-MM-DD');
        if (!schedulesByDate[key]) schedulesByDate[key] = [];
        schedulesByDate[key].push({ ...s, _type: 'ticket' });
    });
    // Project subtasks span a date range — expand each day within the current month
    projectSubTasks.forEach(s => {
        const start = moment.max(moment(s.start_date), moment(monthStart));
        const end   = moment.min(moment(s.end_date),   moment(monthEnd));
        const cur = start.clone();
        while (cur.isSameOrBefore(end, 'day')) {
            const key = cur.format('YYYY-MM-DD');
            if (!schedulesByDate[key]) schedulesByDate[key] = [];
            schedulesByDate[key].push({ ...s, _type: 'project', schedule_date: key });
            cur.add(1, 'day');
        }
    });

    // Build week rows
    const weeks = [];
    let currentWeek = [];
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
        const d = daysInPrevMonth - i;
        const date = prevMonth.date(d);
        currentWeek.push({ date, day: d, isCurrentMonth: false, dateStr: date.format('YYYY-MM-DD') });
    }
    for (let d = 1; d <= daysInMonth; d++) {
        const date = firstDay.date(d);
        currentWeek.push({ date, day: d, isCurrentMonth: true, dateStr: date.format('YYYY-MM-DD') });
        if (currentWeek.length === 7) { weeks.push(currentWeek); currentWeek = []; }
    }
    if (currentWeek.length > 0) {
        const nextMonth = firstDay.add(1, 'month');
        let nd = 1;
        while (currentWeek.length < 7) {
            const date = nextMonth.date(nd);
            currentWeek.push({ date, day: nd, isCurrentMonth: false, dateStr: date.format('YYYY-MM-DD') });
            nd++;
        }
        weeks.push(currentWeek);
    }

    const today = dayjs().format('YYYY-MM-DD');

    const handleEventClick = (e, schedule) => {
        setDetailData(schedule);
        setDetailAnchor(e.currentTarget);
    };
    const handleDetailClose = () => { setDetailAnchor(null); setDetailData(null); };

    const formatDisplayDate = (dayInfo) => {
        if (dayInfo.day === 1 && dayInfo.isCurrentMonth) return `1 ${dayjs().month(month).format('MMM')}`;
        if (!dayInfo.isCurrentMonth) return `${dayInfo.day} ${dayInfo.date.format('MMM')}`;
        return `${dayInfo.day}`;
    };

    return (
        <Box sx={{ backgroundColor: '#fff', borderRadius: '0 0 8px 8px', overflow: 'hidden', border: '1px solid #e0e0e0', borderTop: 'none' }}>

            {/* Legend */}
            <Box sx={{ display: 'flex', gap: 2, px: 2, py: 1.5, borderBottom: '1px solid #e0e0e0', backgroundColor: '#fafafa' }}>
                {Object.entries(TYPE_COLORS).map(([type, c]) => (
                    <Box key={type} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '3px', backgroundColor: c.bg, border: `2px solid ${c.border}`, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#555' }}>{c.label}</Typography>
                    </Box>
                ))}
            </Box>


            {/* Day Headers */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid #e0e0e0' }}>
                {DAY_NAMES.map((day, idx) => (
                    <Box key={idx} sx={{ py: 1, textAlign: 'center', backgroundColor: '#fafafa', borderRight: idx < 6 ? '1px solid #e0e0e0' : 'none' }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#666' }}>{day}</Typography>
                    </Box>
                ))}
            </Box>

            {/* Calendar Weeks */}
            {weeks.map((week, weekIdx) => (
                <Box key={weekIdx} sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                    borderBottom: weekIdx < weeks.length - 1 ? '1px solid #e0e0e0' : 'none',
                    minHeight: '120px',
                }}>
                    {week.map((dayInfo, dayIdx) => {
                        const daySchedules = schedulesByDate[dayInfo.dateStr] || [];
                        const isToday = dayInfo.dateStr === today;
                        const maxDisplay = 3;
                        const remaining = daySchedules.length - maxDisplay;

                        return (
                            <Box key={dayIdx} sx={{
                                borderRight: dayIdx < 6 ? '1px solid #e0e0e0' : 'none',
                                padding: '4px 6px',
                                minHeight: '120px',
                                backgroundColor: dayInfo.isCurrentMonth ? '#fff' : '#fafafa',
                                transition: 'background-color 0.15s ease',
                                '&:hover': { backgroundColor: dayInfo.isCurrentMonth ? '#f5fffe' : '#f5f5f5' },
                                position: 'relative',
                            }}>
                                {/* Date number */}
                                <Typography sx={{
                                    fontSize: '13px',
                                    fontWeight: isToday ? 700 : (dayInfo.isCurrentMonth ? 500 : 400),
                                    color: isToday ? '#fff' : (dayInfo.isCurrentMonth ? '#333' : '#bbb'),
                                    mb: 0.5,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    ...(isToday && { backgroundColor: THEME_COLOR, borderRadius: '50%', width: 26, height: 26 }),
                                }}>
                                    {formatDisplayDate(dayInfo)}
                                </Typography>

                                {/* Event pills */}
                                <Box sx={{ mt: 0.25 }}>
                                    {daySchedules.slice(0, maxDisplay).map((schedule, sIdx) => {
                                        const colors = getColors(schedule);
                                        return (
                                            <Box key={sIdx} onClick={(e) => handleEventClick(e, schedule)} sx={{
                                                backgroundColor: colors.bg,
                                                borderLeft: `3px solid ${colors.border}`,
                                                borderRadius: '2px',
                                                padding: '2px 5px',
                                                mb: 0.5,
                                                cursor: 'pointer',
                                                overflow: 'hidden',
                                                transition: 'all 0.15s ease',
                                                '&:hover': { transform: 'scale(1.02)', boxShadow: '0 1px 4px rgba(0,0,0,0.12)' },
                                            }}>
                                                <Typography sx={{
                                                    fontSize: '11px', fontWeight: 500, color: colors.color,
                                                    lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                                }}>
                                                    {getEventLabel(schedule)}
                                                </Typography>
                                            </Box>
                                        );
                                    })}

                                    {remaining > 0 && (
                                        <Tooltip
                                            placement="bottom-start"
                                            arrow={false}
                                            componentsProps={{
                                                tooltip: {
                                                    sx: {
                                                        p: 1.25,
                                                        bgcolor: '#fff',
                                                        color: 'inherit',
                                                        boxShadow: '0 4px 16px rgba(0,0,0,0.13)',
                                                        borderRadius: '8px',
                                                        border: '1px solid #e8e8e8',
                                                        minWidth: 220,
                                                        maxWidth: 300,
                                                    },
                                                },
                                            }}
                                            title={
                                                <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
                                                    {daySchedules.map((schedule, idx) => {
                                                        const colors = getColors(schedule);
                                                        return (
                                                            <Box key={idx} onClick={(e) => handleEventClick(e, schedule)} sx={{
                                                                backgroundColor: colors.bg,
                                                                borderLeft: `3px solid ${colors.border}`,
                                                                borderRadius: '3px',
                                                                px: 1, py: 0.75,
                                                                mb: idx < daySchedules.length - 1 ? 0.75 : 0,
                                                                cursor: 'pointer',
                                                                '&:hover': { filter: 'brightness(0.96)' },
                                                            }}>
                                                                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: colors.color, lineHeight: 1.3 }}>
                                                                    {getEventLabel(schedule)}
                                                                </Typography>
                                                                <Typography sx={{ fontSize: '11px', color: '#777', mt: 0.25 }}>
                                                                    {(schedule._type === 'opportunity' || schedule._type === 'lead') ? (schedule.meeting_type || 'In Person') : (schedule.status || 'Scheduled')}
                                                                </Typography>
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            }
                                        >
                                            <Typography sx={{
                                                fontSize: '10px', color: THEME_COLOR, fontWeight: 600,
                                                cursor: 'default', mt: 0.25, userSelect: 'none',
                                                display: 'inline-block',
                                                '&:hover': { textDecoration: 'underline' },
                                            }}>
                                                +{remaining} more
                                            </Typography>
                                        </Tooltip>
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            ))}

            {/* Detail click popover — z-index 1600 to sit above Tooltip (1500) */}
            <Popover
                open={Boolean(detailAnchor)}
                anchorEl={detailAnchor}
                onClose={handleDetailClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                style={{ zIndex: 1600 }}
                PaperProps={{ sx: { p: 2, minWidth: 280, maxWidth: 380, borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' } }}
            >
                {detailData && (
                    <Box>
                        {/* Colored header bar with close button */}
                        <Box sx={{
                            display: 'flex', alignItems: 'center', pb: 1.25, mb: 1.5,
                            borderBottom: `2px solid ${getColors(detailData).border}`,
                        }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: getColors(detailData).border, flexShrink: 0, mr: 1 }} />
                            <Typography sx={{ fontWeight: 700, fontSize: '14px', color: getColors(detailData).color, flex: 1 }}>
                                {getColors(detailData).label}
                            </Typography>
                            <IconButton size="small" onClick={handleDetailClose} sx={{ p: 0.25, color: '#999', '&:hover': { color: '#333' } }}>
                                <CloseIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Box>

                        {detailData._type === 'opportunity' ? (
                            <>
                                <DetailRow label="Date" value={moment(detailData.schedule_date).format('DD MMM YYYY')} />
                                <DetailRow label="Client" value={detailData.opportunity?.client?.name || '—'} />
                                <DetailRow label="Opportunity" value={detailData.opportunity?.opportunity_name || '—'} />
                                <DetailRow label="BD Owner / SPOC" value={detailData.bd_owner?.name || '—'} />
                                <DetailRow label="Location" value={detailData.location?.name || detailData.location || '—'} />
                                <DetailRow label="Meeting Type" value={
                                    <Box component="span" sx={{
                                        px: 1, py: 0.25, borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                                        backgroundColor: TYPE_COLORS.opportunity.bg,
                                        color: TYPE_COLORS.opportunity.color,
                                        border: `1px solid ${TYPE_COLORS.opportunity.border}`,
                                    }}>
                                        {detailData.meeting_type || 'In Person'}
                                    </Box>
                                } />
                                {detailData.description && (
                                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #f0f0f0' }}>
                                        <Typography sx={{ fontSize: '11px', color: '#888', fontWeight: 500, mb: 0.5 }}>Description</Typography>
                                        <Typography sx={{ fontSize: '12px', color: '#444', lineHeight: 1.5 }}>{detailData.description}</Typography>
                                    </Box>
                                )}
                            </>
                        ) : detailData._type === 'lead' ? (
                            <>
                                <DetailRow label="Date" value={moment(detailData.schedule_date).format('DD MMM YYYY')} />
                                <DetailRow label="Client" value={detailData.lead?.client_name || '—'} />
                                <DetailRow label="Opportunity" value={detailData.lead?.opportunity_name || '—'} />
                                <DetailRow label="Facility" value={detailData.lead?.facility_name || '—'} />
                                <DetailRow label="BD Owner / SPOC" value={detailData.bd_owner?.name || '—'} />
                                <DetailRow label="Location" value={detailData.location || '—'} />
                                <DetailRow label="Meeting Type" value={
                                    <Box component="span" sx={{
                                        px: 1, py: 0.25, borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                                        backgroundColor: TYPE_COLORS.lead.bg,
                                        color: TYPE_COLORS.lead.color,
                                        border: `1px solid ${TYPE_COLORS.lead.border}`,
                                    }}>
                                        {detailData.meeting_type || 'In Person'}
                                    </Box>
                                } />
                                <DetailRow label="Status" value={detailData.status || 'Open'} />
                                {detailData.description && (
                                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #f0f0f0' }}>
                                        <Typography sx={{ fontSize: '11px', color: '#888', fontWeight: 500, mb: 0.5 }}>Description</Typography>
                                        <Typography sx={{ fontSize: '12px', color: '#444', lineHeight: 1.5 }}>{detailData.description}</Typography>
                                    </Box>
                                )}
                            </>
                        ) : detailData._type === 'ticket' ? (
                            <>
                                <DetailRow label="Title" value={detailData.title || '—'} />
                                <DetailRow label="Assignee" value={detailData.assignee || '—'} />
                                <DetailRow label="Facility" value={detailData.facility?.facility_name || '—'} />
                                <DetailRow label="Scheduled Date" value={moment(detailData.scheduled_date).format('DD MMM YYYY')} />
                                <DetailRow label="Status" value={
                                    <Box component="span" sx={{
                                        px: 1, py: 0.25, borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                                        backgroundColor: TYPE_COLORS.ticket.bg,
                                        color: TYPE_COLORS.ticket.color,
                                        border: `1px solid ${TYPE_COLORS.ticket.border}`,
                                    }}>
                                        {detailData.status}
                                    </Box>
                                } />
                                {detailData.ex_data?.comments && (
                                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #f0f0f0' }}>
                                        <Typography sx={{ fontSize: '11px', color: '#888', fontWeight: 500, mb: 0.5 }}>Comments</Typography>
                                        <Typography sx={{ fontSize: '12px', color: '#444', lineHeight: 1.5 }}>{detailData.ex_data.comments}</Typography>
                                    </Box>
                                )}
                            </>
                        ) : detailData._type === 'project' ? (
                            <>
                                <DetailRow label="Sub Task" value={detailData.sub_task_name || '—'} />
                                <DetailRow label="Task" value={detailData.task?.task_name || '—'} />
                                <DetailRow label="Facility" value={detailData.task?.project?.facility?.facility_name || '—'} />
                                <DetailRow label="Assignee" value={getSpocName(detailData.spoc) || '—'} />
                                <DetailRow label="Date Range" value={
                                    `${moment(detailData.start_date).format('DD MMM')} – ${moment(detailData.end_date).format('DD MMM YYYY')}`
                                } />
                                <DetailRow label="Status" value={
                                    <Box component="span" sx={{
                                        px: 1, py: 0.25, borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                                        backgroundColor: TYPE_COLORS.project.bg,
                                        color: TYPE_COLORS.project.color,
                                        border: `1px solid ${TYPE_COLORS.project.border}`,
                                    }}>
                                        {detailData.status}
                                    </Box>
                                } />
                                {detailData.description && (
                                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #f0f0f0' }}>
                                        <Typography sx={{ fontSize: '11px', color: '#888', fontWeight: 500, mb: 0.5 }}>Description</Typography>
                                        <Typography sx={{ fontSize: '12px', color: '#444', lineHeight: 1.5 }}>{detailData.description}</Typography>
                                    </Box>
                                )}
                            </>
                        ) : (
                            <>
                                <DetailRow label="Date" value={moment(detailData.schedule_date).format('DD MMM YYYY')} />
                                <DetailRow label="Assignee" value={getAssignee(detailData) || '—'} />
                                <DetailRow label="Facility" value={detailData.facility?.facility_name || '—'} />
                                <DetailRow label="Status" value={
                                    <Box component="span" sx={{
                                        px: 1, py: 0.25, borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                                        backgroundColor: getColors(detailData).bg,
                                        color: getColors(detailData).color,
                                        border: `1px solid ${getColors(detailData).border}`,
                                    }}>
                                        {detailData.status || 'Scheduled'}
                                    </Box>
                                } />
                                {detailData.latest_calibration_date && (
                                    <DetailRow label="Last Calibration" value={moment(detailData.latest_calibration_date).format('DD MMM YYYY')} />
                                )}
                            </>
                        )}
                    </Box>
                )}
            </Popover>
        </Box>
    );
}

function DetailRow({ label, value }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', py: 0.6, gap: 1 }}>
            <Typography sx={{ fontSize: '12px', color: '#888', fontWeight: 500, flexShrink: 0 }}>{label}</Typography>
            <Typography sx={{ fontSize: '12px', color: '#333', fontWeight: 500, textAlign: 'right' }}>{value}</Typography>
        </Box>
    );
}
